/**
 * FX rates: 1 GHS = X units of target currency.
 *
 * These are display rates used for pricing pages and cross-currency analytics.
 * They are intentionally conservative for pricing display because non-GHS values
 * are rounded up to the next whole number.
 *
 * Source model: mid-market style references (Google/XE/Wise class rates),
 * reviewed and refreshed for Apr 2026.
 */
const GHS_TO_CURRENCY: Record<string, number> = {
  GHS: 1,
  USD: 0.065,
  GBP: 0.049,
  EUR: 0.057,
  NGN: 101.0,
  KES: 8.43,
  ZAR: 1.21,
};

export const CURRENCY_SYMBOLS: Record<string, string> = {
  GHS: "₵",
  USD: "$",
  GBP: "£",
  EUR: "€",
  NGN: "₦",
  KES: "KSh",
  ZAR: "R",
};

/** Currencies we support for pricing display (public Pricing page) */
export const PRICE_DISPLAY_CURRENCIES = ["GHS", "NGN", "KES", "ZAR", "GBP", "USD", "EUR"] as const;

function normalizeCurrencyCode(currency: string | null | undefined): string {
  return String(currency || "GHS").toUpperCase().trim() || "GHS";
}

function getGhsToRate(currency: string | null | undefined): number | null {
  const code = normalizeCurrencyCode(currency);
  return GHS_TO_CURRENCY[code] ?? null;
}

/**
 * Convert an amount in GHS to the target currency for display.
 * Rounds up to the nearest whole number (e.g. 10.44 → 11) so figures are never understated.
 * Returns the same amount if target is GHS or unknown (no conversion).
 */
export function convertGhsTo(amountGhs: number, targetCurrency: string): number {
  if (!Number.isFinite(amountGhs)) return 0;
  const code = normalizeCurrencyCode(targetCurrency);
  const rate = getGhsToRate(code);
  if (rate == null) return amountGhs;
  const converted = amountGhs * rate;
  return code === "GHS" ? amountGhs : Math.ceil(converted);
}

/**
 * Convert an amount from any supported source currency into GHS.
 * Returns the original amount when the source currency is unknown.
 */
export function convertToGhs(amount: number, sourceCurrency: string | null | undefined): number {
  const code = normalizeCurrencyCode(sourceCurrency);
  if (!Number.isFinite(amount)) return 0;
  if (code === "GHS") return amount;
  const rate = getGhsToRate(code);
  if (rate == null || rate === 0) return amount;
  return amount / rate;
}

/**
 * Convert an amount between any supported currencies.
 * Revenue analytics use this exact converter rather than the rounded pricing helper.
 */
export function convertCurrencyAmount(
  amount: number,
  sourceCurrency: string | null | undefined,
  targetCurrency: string | null | undefined,
): number {
  if (!Number.isFinite(amount)) return 0;
  const from = normalizeCurrencyCode(sourceCurrency);
  const to = normalizeCurrencyCode(targetCurrency);
  if (from === to) return amount;
  const amountGhs = convertToGhs(amount, from);
  if (to === "GHS") return amountGhs;
  const rate = getGhsToRate(to);
  if (rate == null) return amount;
  return amountGhs * rate;
}

export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency?.toUpperCase()] ?? currency ?? "₵";
}

/** Format a plan price (amount in GHS) for display in target currency (rounded up to whole number) */
export function formatPlanPriceDisplay(amountGhs: number, currency: string): string {
  const amount = convertGhsTo(amountGhs, currency);
  const sym = getCurrencySymbol(currency);
  return `${sym}${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}
